import { useState, useEffect, useContext, useRef } from "react";
// import { API } from "aws-amplify";
import { Auth, Hub } from "aws-amplify";
import { logError } from "util/logging";
import { strNonEmpty } from "util/strings";
import { ModalContext } from "contexts/Modal";
import { UserAdminContext } from "components/UserAdmin/UserAdmin-container";

/**
 * Contains the logic for UserAdminUser
 *
 * @version 0.0.1
 * @author Steve Hunley
 */

// Validate that the inputs on the form are valid.
const validateInputs = (inputs, showModal) => {
  const oldPasswordNonEmpty = strNonEmpty(inputs.userOldPassword);
  const newPasswordNonEmpty = strNonEmpty(inputs.userNewPassword);
  const confirmPasswordNonEmpty = strNonEmpty(inputs.userConfirmPassword);

  if (oldPasswordNonEmpty || newPasswordNonEmpty || confirmPasswordNonEmpty) {
    if (!(oldPasswordNonEmpty && newPasswordNonEmpty && confirmPasswordNonEmpty)) {
      showModal("Save User", "If you specify any of the passwords, you must specify all of them.");
      return false;
    }
  }

  if (inputs.userNewPassword !== inputs.userConfirmPassword) {
    showModal("Save User", "New Password and Confirm Password must be the same.");
    return false;
  }

  return true;
};

// Placeholder for future update logic (kept as in original)
const updateUser = async (inputs, showModal) => {
  // Example place to call API.patch once implemented.
  // await Auth.currentAuthenticatedUser();
  // await API.patch(...);
};

const deactivateUser = async (showModal) => {
  try {
    // Example place to call API.del once implemented.
    // await Auth.currentAuthenticatedUser();
    // await API.del(...);
  } catch (err) {
    logError("UserAdminUser-logic.js deactivateUser Error", err);
  }
};

const useUserAdminUser = () => {
  const { userProfile } = useContext(UserAdminContext);
  const { showModal, showConfirm } = useContext(ModalContext);

  const [inputs, setInputs] = useState({});

  // --- Auth readiness + StrictMode-safe guards ---
  const [ready, setReady] = useState(false);
  const inFlight = useRef(false);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (mounted) setReady(true);
      } catch {
        if (mounted) setReady(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (mounted) setReady(true);
        } catch {
          if (mounted) setReady(false);
        }
      } else if (evt === "signOut") {
        if (mounted) setReady(false);
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  const handleSave = async (event) => {
    if (event) event.preventDefault();

    // Ensure session before proceeding
    if (!ready) {
      try {
        await Auth.currentAuthenticatedUser();
        setReady(true);
      } catch {
        showModal("Save User", "You are signed out. Please sign in and try again.");
        return;
      }
    }

    if (inFlight.current) return;
    inFlight.current = true;

    try {
      if (!validateInputs(inputs, showModal)) return;

      if (!inputs.id) {
        logError("UserAdminUser-logic.js: Error occurred while updating profile (3)", null);
        showModal("Save User", "Error occurred while saving user. (3)");
      } else {
        await updateUser(inputs, showModal);
        // showModal("Save User", "Save Successful"); // enable when updateUser is implemented
      }
    } catch (err) {
      logError("UserAdminUser-logic.js: Error occurred while updating profile (2)", err);
      showModal("Save User", "Error occurred while saving user. (4)");
    } finally {
      inFlight.current = false;
    }
  };

  const handleDeactivate = async (event) => {
    if (event) event.preventDefault();

    // Ensure session before proceeding
    if (!ready) {
      try {
        await Auth.currentAuthenticatedUser();
        setReady(true);
      } catch {
        showModal("Deactivate User", "You are signed out. Please sign in and try again.");
        return;
      }
    }

    try {
      showConfirm("Deactivate User", "Do you really want to deactivate the user?", () => {
        deactivateUser(showModal);
      });
    } catch (err) {
      logError("UserAdminUser-logic.js handleDeactivate error: Unknown error", err);
    }
  };

  useEffect(() => {
    if (userProfile && userProfile.id) {
      setInputs({
        id: userProfile.id,
        userName: userProfile.name || "",
        userEmail: userProfile.email,
        userPhone: userProfile.phone || "",
        userOldPassword: "",
        userNewPassword: "",
        userConfirmPassword: "",
      });
    }
  }, [userProfile]);

  return { inputs, setInputs, handleSave, showModal, handleDeactivate };
};

export default useUserAdminUser;
